import { motion } from "framer-motion";
import { Link } from "react-router-dom";
import { ArrowRight } from "lucide-react";

export const fadeUp = {
  initial: { opacity: 0, y: 24 },
  whileInView: { opacity: 1, y: 0 },
  viewport: { once: true, margin: "-60px" },
  transition: { duration: 0.55 },
};

export function SectionHead({ kicker, title, sub, light }) {
  return (
    <motion.div {...fadeUp} className="text-center">
      {kicker && <span className={`text-xs font-bold uppercase tracking-[0.22em] ${light ? "text-[#FF9933]" : "text-shipany-cyan"}`}>{kicker}</span>}
      <h2 className="font-display mt-3 text-2xl font-bold tracking-tight text-white sm:text-3xl lg:text-4xl">{title}</h2>
      {sub && <p className="mx-auto mt-3 max-w-2xl text-sm leading-relaxed text-slate-400 sm:text-base">{sub}</p>}
    </motion.div>
  );
}

export function GradientCta({ title, sub, primary = ["Start Shipping", "/signup"], secondary = ["Talk to Sales", "/contact"], testId = "gradient-cta" }) {
  return (
    <section className="relative overflow-hidden bg-gradient-to-br from-shipany-blue via-[#0A5CA8] to-[#10B981] py-16 sm:py-20" data-testid={testId}>
      <div className="pointer-events-none absolute -left-24 -top-24 h-72 w-72 rounded-full bg-white/10 blur-3xl" />
      <div className="pointer-events-none absolute -bottom-24 -right-24 h-72 w-72 rounded-full bg-[#12C8F5]/20 blur-3xl" />
      <div className="relative mx-auto max-w-3xl px-5 text-center">
        <motion.div {...fadeUp}>
          <h2 className="font-display text-2xl font-bold tracking-tight text-white sm:text-3xl lg:text-4xl">{title}</h2>
          <p className="mt-3 text-sm text-white/80 sm:text-base">{sub}</p>
          <div className="mt-7 flex flex-col items-center justify-center gap-3 sm:flex-row">
            <Link to={primary[1]} data-testid={`${testId}-primary-btn`}
              className="flex w-full items-center justify-center gap-2 rounded-xl bg-white px-8 py-3.5 font-display text-sm font-bold text-shipany-blue transition-all hover:shadow-[0_10px_36px_rgba(255,255,255,0.35)] sm:w-auto">
              {primary[0]} <ArrowRight className="h-4 w-4" />
            </Link>
            <Link to={secondary[1]} data-testid={`${testId}-secondary-btn`}
              className="flex w-full items-center justify-center gap-2 rounded-xl border-2 border-white/40 px-8 py-3.5 font-display text-sm font-bold text-white transition-colors hover:bg-white/10 sm:w-auto">
              {secondary[1] === "/contact" ? secondary[0] : secondary[0]}
            </Link>
          </div>
        </motion.div>
      </div>
    </section>
  );
}

export function MarketingHero({ kicker, title, sub, children, gradient }) {
  return (
    <section className={`relative overflow-hidden py-16 sm:py-24 ${gradient ? "bg-gradient-to-br from-shipany-blue via-[#071B3A] to-[#0B7E5E]" : "bg-shipany-navy"}`} data-testid="page-hero">
      <div className="pointer-events-none absolute inset-0 hero-grid opacity-40" />
      <div className="pointer-events-none absolute -right-32 -top-32 h-96 w-96 rounded-full bg-shipany-cyan/10 blur-3xl" />
      <div className="pointer-events-none absolute -bottom-32 -left-32 h-96 w-96 rounded-full bg-[#FF9933]/10 blur-3xl" />
      <div className="relative mx-auto max-w-7xl px-5">
        <motion.div {...fadeUp} className={children ? "" : "text-center"}>
          {kicker && <span className="text-xs font-bold uppercase tracking-[0.22em] text-shipany-cyan">{kicker}</span>}
          <h1 className="font-display mt-3 text-4xl font-extrabold tracking-tight text-white sm:text-5xl lg:text-6xl">{title}</h1>
          {sub && <p className={`mt-4 max-w-2xl text-base leading-relaxed text-slate-300 sm:text-lg ${children ? "" : "mx-auto"}`}>{sub}</p>}
        </motion.div>
        {children}
      </div>
    </section>
  );
}
